import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCheck,
  faCircleNotch,
  faEnvelope,
  faEye,
  faEyeSlash,
  faLock,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import './LoginPage.css';

const SITE_URL = 'https://leveluptodo.com.br';
const EMAIL_SUPORTE = 'contato@leveluptodo.com.br';

// Os mesmos argumentos da landing: quem chega aqui vindo do site continua
// lendo a mesma conversa, em vez de cair num formulario mudo.
const VANTAGENS = [
  ['Grátis para começar', 'sem cartão, sem prazo.'],
  ['Vira app no celular', 'com notificação e captura offline.'],
  ['Pessoal e trabalho separados', 'em workspaces próprios.'],
];

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [expirado, setExpirado] = useState(false);

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (params.get('expired')) {
      setExpirado(true);
    }
  }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (enviando) return;

    setErro('');
    setExpirado(false);
    setEnviando(true);

    try {
      const resultado = await login(email.trim(), senha);

      if (resultado.success) {
        navigate('/dashboard');
        return;
      }

      // Erro no proprio card, nao em alert(): o alert some do fluxo e obriga
      // um clique extra so para voltar ao formulario.
      setErro(resultado.message || 'Não foi possível entrar. Confira e-mail e senha.');
    } catch (error) {
      console.error('Erro no login:', error);
      setErro('Não conseguimos falar com o servidor. Tente de novo em instantes.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <header className="login-top">
        <a className="login-brand" href={SITE_URL}>
          <span className="login-brand-mark">
            <FontAwesomeIcon icon={faBolt} />
          </span>
          LevelUp<span>ToDo</span>
        </a>
      </header>

      <main className="login-main">
        <div className="login-card">

          <aside className="login-aside">
            <div>
              <h1>O sistema que junta a sua vida inteira num lugar só.</h1>
              <p className="login-lead">
                Projetos, rotina, hábitos e metas — pessoal e profissional — com XP para
                provar que você avançou.
              </p>
            </div>

            <div className="login-xp">
              <div className="login-xp-top">
                <b>Nível 4 · Especialista</b>
                <span>3.480 XP</span>
              </div>
              <div className="login-xp-track">
                <div className="login-xp-fill" />
              </div>
            </div>

            <ul className="login-perks">
              {VANTAGENS.map(([titulo, resto]) => (
                <li key={titulo}>
                  <FontAwesomeIcon icon={faCheck} />
                  <span><b>{titulo}</b> — {resto}</span>
                </li>
              ))}
            </ul>
          </aside>

          <section className="login-form-side">
            <div className="login-heading">
              <h2>Bem-vindo de volta</h2>
              <p>Seu dia está esperando por você.</p>
            </div>

            {expirado && (
              <div className="login-aviso" role="status">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span>Sua sessão expirou. Entre de novo para continuar.</span>
              </div>
            )}

            {erro && (
              <div className="login-aviso login-aviso-erro" role="alert">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span>{erro}</span>
              </div>
            )}

            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <div className="login-field">
                <label htmlFor="login-email">E-mail</label>
                <div className="login-input">
                  <FontAwesomeIcon icon={faEnvelope} />
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@email.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="login-senha">Senha</label>
                <div className="login-input">
                  <FontAwesomeIcon icon={faLock} />
                  <input
                    id="login-senha"
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="sua senha"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="login-peek"
                    onClick={() => setMostrarSenha((atual) => !atual)}
                    aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    <FontAwesomeIcon icon={mostrarSenha ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="login-field-row">
                <span className="login-tiny">Sua sessão fica ativa por 30 dias.</span>
                <a className="login-link" href={`mailto:${EMAIL_SUPORTE}?subject=Esqueci minha senha`}>
                  Esqueci minha senha
                </a>
              </div>

              <button type="submit" className="login-btn" disabled={enviando}>
                {enviando ? (
                  <>
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>

            <p className="login-foot">
              Ainda não tem conta? <a className="login-link" href={SITE_URL}>Criar agora, é grátis</a>
            </p>
          </section>

        </div>
      </main>

      <footer className="login-bottom">
        © {new Date().getFullYear()} LevelUp ToDo
      </footer>
    </div>
  );
};

export default LoginPage;
